import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

type Relay = (message: string, kind: 'error' | 'success') => void;

/**
 * While `enabled`, string toasts raised anywhere in the tree are handed to
 * `relay` instead of being shown.
 *
 * The BankID flow toasts from shared places — `features/bankid/bankid.slice`,
 * `BankIDComponent`, `useBankID` — that the invoice and ID-verification flows
 * rely on too. Rather than edit those, the auth pages borrow their toasts for
 * as long as they are mounted and give them to the guide character; the
 * originals are put back on unmount.
 *
 * Non-string content (a React node) is left alone, so rich toasts elsewhere
 * are never swallowed.
 *
 * Returns the untouched toast functions, captured before any interception, for
 * the cases where a caller wants a real toast *as well as* the relay — the
 * sign-up submit does this, so a failed registration is impossible to miss.
 */
export const useToastRelay = (enabled: boolean, relay: Relay) => {
    /* Read on first render, which is before the effect below swaps them out. */
    const pristine = useRef({ error: toast.error, success: toast.success });

    useEffect(() => {
        if (!enabled) return;

        const original = {
            error: toast.error,
            success: toast.success,
            info: toast.info,
            warn: toast.warn,
        };

        const intercept = (kind: 'error' | 'success', fallback: any) =>
            ((content: any, options: any) => {
                if (typeof content !== 'string') return fallback(content, options);
                relay(content, kind);
                return '' as any;
            }) as any;

        toast.error = intercept('error', original.error);
        toast.success = intercept('success', original.success);
        toast.info = intercept('success', original.info);
        toast.warn = intercept('error', original.warn);

        return () => {
            toast.error = original.error;
            toast.success = original.success;
            toast.info = original.info;
            toast.warn = original.warn;
        };
    }, [enabled, relay]);

    return pristine.current;
};

export default useToastRelay;
